import { push, go } from 'connected-react-router';
import rfdc from 'rfdc';

import apiActionCreator from '../../services/middleware/actionCreator';
import * as microservicesTypes from '../../services/middleware/microservicesTypes';
import * as ActionTypes from './types/responses';
import * as activeProjectTypes from './types/activeProject';

import SurveyUtils from '../../utils/survey.utils';
import { handleResponseErrors, redirectSurvey, getScreenSize } from './helpers';
import { PAGE_TYPES } from '../../components/types/survey';

import { isConjoint2Experiment, isConjointExperiment, isMaxDiffExperiment } from '../../components/utils/helpers';
import { setEndOfSurveyPageText } from '../slices/endOfSurveyPage/endOfSurveyPage';

const cloneDeep = rfdc();

export const onChangeItem = (frontendId, value, subtype) => ({
  type: ActionTypes.ON_CHANGE_ITEM,
  payload: {
    frontendId,
    value,
    subtype,
  },
});

export const clearQuestionResponses = (questionsFrontendId) => ({
  type: ActionTypes.CLEAR_QUESTION_RESPONSES,
  payload: { questionsFrontendId },
});

export const addError = (frontendId, error) => ({
  type: ActionTypes.ADD_ERROR,
  payload: {
    frontendId,
    error,
  },
});

export const removeErrors = () => ({
  type: ActionTypes.REMOVE_ERRORS,
});

export const submitResponse = (
  { answers, createdAt, isAdmin, hash },
  questionsViewed = [],
  recaptchaToken = ''
) => async (dispatch, getState) => {
  const {
    responses,
    activeProject: { items = {}, survey: { currentPage, userData, breadcrumbs: path } = {} } = {},
    clipboardPaste,
  } = getState();

  const data = {
    answers: SurveyUtils.convertFrontendIdToIds(answers, items),
    currentPage,
    userData,
    createdAt,
    path,
    recaptchaToken,
    clipboardPaste,
  };
  const clonedData = cloneDeep(data);

  return dispatch(
    apiActionCreator({
      endpoint: '/submit',
      authenticated: false,
      types: [
        ActionTypes.SUBMIT_RESPONSES_REQUEST,
        [
          (response) => {
            const { page = {}, data = {}, type } = response;

            if (PAGE_TYPES.SCREEN_OUT === type) {
              return dispatch(redirectSurvey(data, activeProjectTypes.SURVEY_FETCH_NEXT_PAGE_FAILED));
            }

            if (page.type === PAGE_TYPES.REDIRECT) {
              window.location = page.redirectTo;
              return {
                type: ActionTypes.REDIRECT_SUBMIT,
              };
            }

            // Thank you page
            if (page.endOfSurveyText) {
              dispatch(setEndOfSurveyPageText(page.endOfSurveyText));
            }

            if (isAdmin) {
              dispatch(go(`/${hash}?admin=true`));
            } else {
              dispatch(push(`/complete`));
            }

            return {
              type: ActionTypes.SUBMIT_RESPONSES_SUCCESS,
            };
          },
        ],
        [
          (response) => {
            dispatch(handleResponseErrors(response));
            return {
              type: ActionTypes.SUBMIT_RESPONSES_FAILED,
            };
          },
        ],
      ],
      data: {
        ...clonedData,
        ...getScreenSize(),
      },
      microservice: microservicesTypes.SURVEY_SERVICE,
      method: 'POST',
    })
  );
};

export const clearResponses = () => {
  return {
    type: ActionTypes.CLEAR_RESPONSES,
  };
};

export const clearResponse = (frontendId) => {
  return {
    type: ActionTypes.CLEAR_RESPONSE,
    payload: { frontendId },
  };
};

export const changeLanguage = (language) => {
  return {
    type: ActionTypes.CAPTURE_CHANGE_LANGUAGE,
    payload: language,
  };
};

export const clearDependentQuestions = (changedFrontendId) => (dispatch, getState) => {
  const { activeProject: { items = {} } = {} } = getState();
  const changedItem = items[changedFrontendId] || {};
  dispatch({
    type: 'displayedSubItems/removeDisplayedSubItemsByQuestions',
    payload: changedItem.dependentQuestions,
  });
  return dispatch({
    type: ActionTypes.CLEAR_DEPENDENT_QUESTIONS,
    payload: { changedFrontendId, items },
  });
};

export const loadResponses = (responses) => (dispatch, getState) => {
  const { activeProject: { items = {} } = {} } = getState();
  const experiments = Object.values(items).filter(
    (item) => isConjointExperiment(item) || isMaxDiffExperiment(item) || isConjoint2Experiment(item)
  );

  for (const item of experiments) {
    const { experiment: { index } = {} } = item;
    const aggregatedResponse = responses[item.originalFrontendId];
    if (!isConjoint2Experiment(item)) {
      responses[item.frontendId] = aggregatedResponse;
    }

    if (isMaxDiffExperiment(item) && aggregatedResponse && aggregatedResponse.setValues) {
      responses[`${item.frontendId}_${index}`] = aggregatedResponse.setValues[index];
    }

    if (isConjointExperiment(item)) {
      responses[`${item.frontendId}_${index}`] = {
        na: aggregatedResponse.na,
        values: aggregatedResponse.values[index],
      };
    }

    if (isConjoint2Experiment(item)) {
      if (
        responses[item.originalFrontendId] &&
        responses[item.originalFrontendId].values &&
        responses[item.originalFrontendId].values.length >= 1 &&
        responses[item.originalFrontendId].values[0].productSelected
      ) {
        const values = responses[item.originalFrontendId].values.map((value) => {
          return value.productSelected.position;
        });
        responses[item.originalFrontendId].values = values;
      }
    }
  }

  if (experiments.length > 0) {
    dispatch({
      type: ActionTypes.SET_EXPERIMENTS_CONFIG,
      payload: {
        experiments: experiments.map((item) => item.frontendId),
        responses,
      },
    });
  }

  return dispatch({
    type: ActionTypes.LOAD_RESPONSES,
    payload: responses,
  });
};
